import { Button, Checkbox, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { UserResponse } from '@/api/generated/models';
import {
  getGetTeamQueryKey,
  getListMembersQueryKey,
  useAssignMember
} from '@/api/generated/departments-teams/departments-teams';
import { getListUsersQueryKey, useListUsers } from '@/api/generated/users/users';
import { FormModal, ModalFooter, MutationButton, TextField } from '@/components/common';
import { successNotificationIcon } from '@/theme/notificationIcons';

interface AddMemberModalProps {
  opened: boolean;
  onClose: () => void;
  departmentId: string;
  teamId: string;
}

export const AddMemberModal = ({ opened, onClose, departmentId, teamId }: AddMemberModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);

  const { data } = useListUsers(undefined, {
    query: { enabled: opened, refetchOnMount: 'always' }
  });

  // Korisnik je u NAJVIŠE jednom timu (users.team_id je jedan FK, ne
  // many-to-many) — tko je već negdje raspoređen mora prvo biti uklonjen iz
  // tog tima (na NJEGOVOJ detalj-stranici) prije nego postane dostupan ovdje.
  const query = search.trim().toLowerCase();
  const candidates: UserResponse[] = (data ?? []).filter((user) => {
    if (!user.id || user.teamId) return false;
    if (!query) return true;
    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.toLowerCase();
    return fullName.includes(query) || user.email?.toLowerCase().includes(query);
  });

  const toggle = (id?: string) => {
    if (!id) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reset = () => {
    setSearch('');
    setSelected(new Set());
  };

  const assignMutation = useAssignMember();

  // Sekvencijalno, isti obrazac kao OfferItemPickerModal — nema jamstva da su
  // paralelni pozivi na dodjelu sigurni pod istim RLS/tenant kontekstom.
  const handleAdd = async () => {
    setIsAdding(true);
    try {
      for (const userId of selected) {
        await assignMutation.mutateAsync({ id: departmentId, teamId, userId });
      }
      queryClient.invalidateQueries({ queryKey: getListMembersQueryKey(departmentId, teamId) });
      queryClient.invalidateQueries({ queryKey: getGetTeamQueryKey(departmentId, teamId) });
      queryClient.invalidateQueries({ queryKey: getListUsersQueryKey() });
      notifications.show({
        color: 'green',
        icon: successNotificationIcon,
        message: t('departments.team.addMemberSuccess', { count: selected.size })
      });
      reset();
      onClose();
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <FormModal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      size={640}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('departments.team.addMember')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        <TextField
          hideLabel
          placeholder={t('departments.team.searchUsersPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />

        <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
          {t('departments.team.addMemberHint')}
        </Text>

        <Stack gap={0} style={{ maxHeight: 320, overflowY: 'auto' }}>
          {candidates.length === 0 && (
            <Text fz={13} py={24} ta="center" style={{ color: 'var(--color-text-muted)' }}>
              {t('departments.team.noCandidates')}
            </Text>
          )}

          {candidates.map((user, index) => (
            <Group
              key={user.id}
              gap={10}
              wrap="nowrap"
              py={10}
              role="button"
              tabIndex={0}
              onClick={() => toggle(user.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') toggle(user.id);
              }}
              style={{
                cursor: 'pointer',
                borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
              }}
            >
              <Checkbox
                checked={!!user.id && selected.has(user.id)}
                onChange={() => toggle(user.id)}
                onClick={(event) => event.stopPropagation()}
              />
              <Stack gap={0}>
                <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                  {user.firstName} {user.lastName}
                </Text>
                <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                  {user.email}
                </Text>
              </Stack>
            </Group>
          ))}
        </Stack>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {t('common.cancel')}
          </Button>
          <MutationButton isPending={isAdding} disabled={selected.size === 0} onClick={handleAdd}>
            {t('departments.team.addSelected', { count: selected.size })}
          </MutationButton>
        </ModalFooter>
      </Stack>
    </FormModal>
  );
};
